/**
 * "A new version is ready" (18 Sep 2026).
 *
 * The bundle carries the id of the build it came from; the server carries the
 * id of the build it is serving, in /version.json. When they differ, this app
 * is stale: the phone kept a shell from before the last deploy. The banner
 * offers a reload at once, and takes it on the next page change on its own —
 * a page change is a natural break, never the middle of a form.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui';

const EVERY_MS = 3 * 60 * 1000;

async function servedBuild(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const body = (await response.json()) as { build?: string };
    return typeof body.build === 'string' ? body.build : null;
  } catch {
    return null;
  }
}

export function UpdateBanner() {
  const [stale, setStale] = useState(false);
  const location = useLocation();
  const seenPath = useRef(location.pathname);

  useEffect(() => {
    if (typeof __BUILD_ID__ !== 'string' || import.meta.env.DEV) return;
    let cancelled = false;
    const check = async () => {
      const build = await servedBuild();
      if (!cancelled && build && build !== __BUILD_ID__) setStale(true);
    };
    void check();
    const timer = window.setInterval(() => void check(), EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The next page change takes the new version on its own.
  useEffect(() => {
    if (location.pathname === seenPath.current) return;
    seenPath.current = location.pathname;
    if (stale) window.location.reload();
  }, [location.pathname, stale]);

  if (!stale) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-line bg-accent-soft px-4 py-2 text-caption text-ink print-hide"
    >
      <span>A new version of the app is ready. It loads on your next page, or reload now.</span>
      <Button size="sm" variant="primary" onClick={() => window.location.reload()}>
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
        Reload
      </Button>
    </div>
  );
}

export default UpdateBanner;
